/**
 * Procedural fighter construction and posing.
 *
 * The combat skeleton is deliberately authored from primitives: no model, texture, animation,
 * or motion-capture asset enters the runtime. The hierarchy is articulated at shoulders, elbows,
 * hips and knees, with separate hands, fingers, feet, costume layers and facial landmarks.
 */

import * as THREE from 'three';
import { FIXED_STEP, lerp, clamp } from '../core/index.js';
import { sampleGaitTarget } from './gait.js';

const PALETTES = Object.freeze({
  kestrel: {
    skin: 0x936e5d, skinLight: 0xbc8f76, cloth: 0x111c28, cloth2: 0x243b4c,
    accent: 0x57f1dd, trim: 0xd7eef0, leather: 0x192630, sole: 0x080c10,
    emissive: 0x0bd8c6,
  },
  bulwark: {
    skin: 0x754a36, skinLight: 0x9c684c, cloth: 0x342724, cloth2: 0x67372a,
    accent: 0xff982f, trim: 0xb8ada0, leather: 0x4a342c, sole: 0x0c0b0a,
    emissive: 0xff641e,
  },
});

const IK_RESULT = { upper: 0, lower: 0 };
const GAIT_TARGET = { x: 0, y: 0 };
const THROW_PHASE = { catchAmount: 0, progress: 0, lift: 0, turn: 0, impact: 0 };
const THROW_VICTIM_POSE = { x: 0, y: 0, roll: 0, twist: 0, yaw: 0, compression: 0 };
const THROW_GRIP_TARGET = { x: 0, y: 0 };
const LOCOMOTION_STATES = new Set(['walk-fwd', 'walk-back', 'dash-fwd', 'dash-back']);
const CHEST_PROFILE = Object.freeze([
  Object.freeze([0, 0.9]),
  Object.freeze([0.22, 1.1]),
  Object.freeze([0.58, 0.96]),
  Object.freeze([1, 0.72]),
]);
const ABDOMEN_PROFILE = Object.freeze([
  Object.freeze([0, 0.84]),
  Object.freeze([0.36, 0.78]),
  Object.freeze([0.72, 0.9]),
  Object.freeze([1, 1]),
]);
const UPPER_ARM_PROFILE = Object.freeze([
  Object.freeze([0, 0.94]),
  Object.freeze([0.24, 1.08]),
  Object.freeze([0.62, 0.93]),
  Object.freeze([1, 0.82]),
]);
const FOREARM_PROFILE = Object.freeze([
  Object.freeze([0, 0.9]),
  Object.freeze([0.24, 1.05]),
  Object.freeze([0.62, 0.88]),
  Object.freeze([1, 0.7]),
]);
const THIGH_PROFILE = Object.freeze([
  Object.freeze([0, 1]),
  Object.freeze([0.22, 1.1]),
  Object.freeze([0.64, 0.92]),
  Object.freeze([1, 0.8]),
]);
const CALF_PROFILE = Object.freeze([
  Object.freeze([0, 0.9]),
  Object.freeze([0.22, 1.04]),
  Object.freeze([0.58, 0.9]),
  Object.freeze([1, 0.64]),
]);
const FINGER_PROXIMAL_PROFILE = Object.freeze([
  Object.freeze([0, 0.96]),
  Object.freeze([0.38, 1.05]),
  Object.freeze([1, 0.8]),
]);
const FINGER_DISTAL_PROFILE = Object.freeze([
  Object.freeze([0, 0.9]),
  Object.freeze([0.42, 0.96]),
  Object.freeze([0.8, 0.78]),
  Object.freeze([1, 0.52]),
]);
const THUMB_PROFILE = Object.freeze([
  Object.freeze([0, 0.96]),
  Object.freeze([0.42, 1.05]),
  Object.freeze([1, 0.64]),
]);
const FINGER_LENGTH_SCALES = Object.freeze([0.82, 1, 0.96, 0.78]);

export function createFighterVisual(stats, bag, materialLibrary = null) {
  const isKestrel = stats.id === 'kestrel';
  const p = PALETTES[stats.id] || PALETTES.kestrel;
  const root = new THREE.Group();
  root.name = `fighter:${stats.id}`;
  const rig = { stats, root, isKestrel };
  root.userData.rig = rig;

  const mats = createMaterials(p, bag, materialLibrary);
  rig.materials = mats;
  const h = stats.height;
  const shoulder = isKestrel ? 0.44 : 0.68;
  const hip = isKestrel ? 0.19 : 0.27;
  const torsoH = isKestrel ? h * 0.34 : h * 0.33;
  const torsoDepth = isKestrel ? 0.27 : 0.43;
  const upperArm = isKestrel ? h * 0.16 : h * 0.145;
  const foreArm = isKestrel ? h * 0.15 : h * 0.14;
  const upperLeg = isKestrel ? h * 0.235 : h * 0.22;
  const lowerLeg = isKestrel ? h * 0.225 : h * 0.22;
  const pelvisY = h * 0.49;
  Object.assign(rig, {
    h, shoulder, hip, torsoH, torsoDepth, upperArm, foreArm, upperLeg, lowerLeg, pelvisY,
    restAnkleY: pelvisY - upperLeg - lowerLeg,
  });

  const body = new THREE.Group();
  body.name = 'pelvis';
  body.position.y = pelvisY;
  root.add(body);
  rig.body = body;

  // Pelvis and layered torso. The shells overlap intentionally like clothing over anatomy.
  mesh(body, boxGeo(isKestrel ? 0.32 : 0.5, h * 0.115, isKestrel ? 0.36 : 0.54, bag),
    mats.cloth, 0, 0.035, 0, true);
  const abdomen = mesh(body,
    profiledRadialGeo(
      torsoH * 0.48,
      isKestrel ? 0.15 : 0.235,
      ABDOMEN_PROFILE,
      isKestrel ? 8 : 10,
      true,
      bag,
    ),
    mats.cloth2, 0, torsoH * 0.24, 0, true);
  abdomen.scale.set(1.0, 1.0, isKestrel ? 1.18 : 1.3);
  rig.abdomen = abdomen;

  const chest = mesh(body,
    profiledRadialGeo(
      torsoH * 0.46,
      isKestrel ? 0.18 : 0.28,
      CHEST_PROFILE,
      isKestrel ? 8 : 10,
      true,
      bag,
    ),
    mats.cloth, 0, torsoH * 0.55, 0, true);
  chest.scale.set(isKestrel ? 0.86 : 1.12, 1, isKestrel ? 1.45 : 1.5);
  rig.chest = chest;

  if (isKestrel) buildKestrelTorso(body, rig, mats, bag);
  else buildBulwarkTorso(body, rig, mats, bag);

  buildHead(body, rig, mats, bag);
  rig.arms = {
    left: buildArm(body, rig, mats, bag, 1),
    right: buildArm(body, rig, mats, bag, -1),
  };
  rig.legs = {
    left: buildLeg(body, rig, mats, bag, 1),
    right: buildLeg(body, rig, mats, bag, -1),
  };

  if (isKestrel) buildSash(body, rig, mats, bag);
  else buildBelt(body, rig, mats, bag);

  root.traverse((node) => {
    if (!node.isMesh) return;
    // Only the three major body masses opt into shadow casting at construction time. Casting a
    // separate shadow draw for every finger, wrap ring, facial landmark and buckle added ~100
    // calls without a visible silhouette benefit. All pieces still receive the shared shadow.
    node.receiveShadow = true;
  });
  return root;
}

function createMaterials(p, bag, library) {
  const standard = (color, roughness, metalness = 0, extra = {}) => {
    const m = new THREE.MeshStandardMaterial({ color, roughness, metalness, ...extra });
    bag.material(m);
    return m;
  };
  const procedural = (kind, color, extra = {}) => library
    ? library.material(kind, { color, ...extra })
    : null;
  return {
    skin: procedural('skin', p.skin) || standard(p.skin, 0.58, 0.0),
    skinLight: procedural('skin', p.skinLight) || standard(p.skinLight, 0.54, 0.0),
    cloth: procedural('cloth', p.cloth) || standard(p.cloth, 0.88, 0.0),
    cloth2: procedural('cloth', p.cloth2) || standard(p.cloth2, 0.72, 0.03),
    accent: procedural('paintedMetal', p.accent, {
      emissive: p.emissive, emissiveIntensity: 0.18,
    }) || standard(p.accent, 0.34, 0.18, { emissive: p.emissive, emissiveIntensity: 0.18 }),
    trim: procedural('brushedMetal', p.trim) || standard(p.trim, 0.42, 0.35),
    leather: procedural('leather', p.leather) || standard(p.leather, 0.62, 0.05),
    sole: procedural('rubber', p.sole) || standard(p.sole, 0.78, 0.0),
    eye: standard(0xc6ffff, 0.2, 0.0, { emissive: p.emissive, emissiveIntensity: 2.4 }),
    dark: procedural('leather', 0x11161d) || standard(0x07090c, 0.62, 0.1),
    metal: procedural('brushedMetal', 0x697783) || standard(0x56606a, 0.31, 0.78),
  };
}

function buildKestrelTorso(body, rig, m, bag) {
  const h = rig.h;
  const collar = mesh(body, torusGeo(0.18, 0.025, 7, 16, bag), m.trim, 0, rig.torsoH * 0.79, 0);
  collar.rotation.x = Math.PI / 2;
  // Crossing wrap panels make the costume read as layered rather than a painted capsule.
  for (const side of [-1, 1]) {
    const wrap = mesh(
      body,
      layeredPanelGeo(0.035, h * 0.25, 0.155, 0.64, 0.16, bag),
      side < 0 ? m.accent : m.cloth2,
      0.145,
      rig.torsoH * 0.5,
      side * 0.055,
    );
    wrap.rotation.x = side * 0.36;
    wrap.rotation.z = side * 0.06;
  }
  const hood = mesh(body, kestrelHoodGeo(h * 0.088, 14, 10, bag),
    m.cloth, 0, rig.torsoH * 0.98, 0);
  hood.scale.set(1.07, 1.1, 1.08);
  rig.hood = hood;
}

function buildBulwarkTorso(body, rig, m, bag) {
  const h = rig.h;
  // Narrow bands follow the visible X/Y contour at the side-on combat camera. The old pieces
  // spanned the full torso depth, so their intersection read as two floating chest slabs.
  const strapA = mesh(body, layeredPanelGeo(0.045, h * 0.245, 0.09, 0.7, 1.35, bag),
    m.leather, 0.255, rig.torsoH * 0.5, 0.018);
  strapA.rotation.z = 0.32;
  const strapB = mesh(body, layeredPanelGeo(0.045, h * 0.245, 0.09, 0.7, 1.35, bag),
    m.leather, 0.258, rig.torsoH * 0.5, -0.018);
  strapB.rotation.z = -0.32;
  mesh(body, boxGeo(0.07, 0.1, 0.14, bag), m.accent, 0.31, rig.torsoH * 0.48, 0);
  for (const side of [-1, 1]) {
    const plate = mesh(body, armorPlateGeo(0.36, 0.12, 0.3, bag), m.metal,
      0, rig.torsoH * 0.68, side * rig.shoulder * 0.82);
    plate.rotation.x = side * 0.08;
    plate.rotation.z = side * 0.06;
  }
}

function buildHead(body, rig, m, bag) {
  const h = rig.h;
  const head = new THREE.Group();
  head.name = 'head';
  head.position.y = rig.torsoH * 0.94;
  body.add(head);
  rig.head = head;

  const neck = mesh(head, cylinderGeo(h * 0.038, h * 0.048, h * 0.075, 10, bag),
    m.skin, 0, -h * 0.045, 0);
  neck.rotation.y = Math.PI / 2;
  const skull = mesh(head, shapedHeadGeo(
    h * (rig.isKestrel ? 0.066 : 0.073),
    rig.isKestrel,
    16,
    12,
    bag,
  ),
    m.skinLight, 0, h * 0.035, 0);
  skull.scale.set(rig.isKestrel ? 0.88 : 0.94, 1.08, rig.isKestrel ? 0.9 : 1.03);
  rig.brows = [];
  rig.jaw = null;
  // Nose, brows, ears and jaw keep close shots from reading as a blank sphere.
  mesh(head, coneGeo(h * 0.015, h * 0.045, 7, bag), m.skinLight, h * 0.067, h * 0.04, 0)
    .rotation.z = -Math.PI / 2;
  for (const side of [-1, 1]) {
    const ear = mesh(head, sphereGeo(h * 0.015, 8, 6, bag), m.skin, 0, h * 0.035, side * h * 0.066);
    ear.scale.x = 0.45;
    const brow = mesh(head, boxGeo(h * 0.012, h * 0.012, h * 0.047, bag),
      rig.isKestrel ? m.dark : m.leather, h * 0.058, h * 0.065, side * h * 0.029);
    brow.rotation.x = side * 0.08;
    brow.userData.expressionSide = side;
    brow.userData.restRotationX = brow.rotation.x;
    brow.userData.restY = brow.position.y;
    rig.brows.push(brow);
  }
  if (rig.isKestrel) {
    // Angular half-mask and a luminous eye slit under the hood.
    const mask = mesh(head, facePlateGeo(h * 0.026, h * 0.057, h * 0.105, 0.7, bag),
      m.dark, h * 0.058, h * 0.018, 0);
    mask.rotation.z = -0.08;
    mesh(head, boxGeo(h * 0.009, h * 0.012, h * 0.092, bag),
      m.eye, h * 0.072, h * 0.061, 0);
  } else {
    const jaw = mesh(head, jawBlockGeo(h * 0.055, h * 0.052, h * 0.11, 0.8, bag),
      m.skin, h * 0.012, -h * 0.012, 0);
    jaw.rotation.z = -0.04;
    jaw.userData.restRotationZ = jaw.rotation.z;
    jaw.userData.restY = jaw.position.y;
    rig.jaw = jaw;
    // Shaved-side scalp plate and blunt beard wedge.
    const scalp = mesh(head, sphereGeo(h * 0.075, 14, 8, bag), m.dark, -h * 0.008, h * 0.052, 0);
    scalp.scale.set(0.88, 0.52, 0.98);
    mesh(head, jawBlockGeo(h * 0.028, h * 0.052, h * 0.092, 0.62, bag),
      m.leather, h * 0.056, -h * 0.035, 0);
    // Orange optic on one temple breaks the symmetry.
    mesh(head, boxGeo(h * 0.012, h * 0.018, h * 0.035, bag),
      m.eye, h * 0.072, h * 0.05, -h * 0.035);
  }
}

function buildArm(body, rig, m, bag, side) {
  const h = rig.h;
  const shoulder = new THREE.Group();
  shoulder.name = side > 0 ? 'shoulderL' : 'shoulderR';
  shoulder.position.set(0, rig.torsoH * 0.66, side * rig.shoulder);
  body.add(shoulder);
  const armRadius = rig.isKestrel ? h * 0.035 : h * 0.052;
  const shoulderCap = mesh(
    shoulder,
    sphereGeo(armRadius * (rig.isKestrel ? 1.12 : 1.14), rig.isKestrel ? 9 : 11, 8, bag),
    rig.isKestrel ? m.cloth2 : m.skin,
    0,
    -armRadius * 0.32,
    0,
  );
  shoulderCap.scale.set(
    rig.isKestrel ? 1.02 : 1.05,
    rig.isKestrel ? 0.8 : 0.78,
    rig.isKestrel ? 1.06 : 1.08,
  );
  mesh(shoulder, profiledRadialGeo(
    rig.upperArm,
    armRadius,
    UPPER_ARM_PROFILE,
    rig.isKestrel ? 8 : 10,
    false,
    bag,
  ), m.skin);
  const elbow = new THREE.Group();
  elbow.name = side > 0 ? 'elbowL' : 'elbowR';
  elbow.position.y = -rig.upperArm;
  shoulder.add(elbow);
  mesh(elbow, sphereGeo(armRadius * 1.05, 9, 7, bag), m.skin, 0, 0, 0);
  mesh(elbow, profiledRadialGeo(
    rig.foreArm,
    armRadius * (rig.isKestrel ? 0.92 : 1.18),
    FOREARM_PROFILE,
    rig.isKestrel ? 8 : 10,
    false,
    bag,
  ), rig.isKestrel ? m.cloth2 : m.skin);
  // Forearm wraps are separate rings, so Bulwark's arms read as heavily reinforced.
  const wrapCount = rig.isKestrel ? 2 : 4;
  for (let i = 0; i < wrapCount; i++) {
    const ring = mesh(elbow, torusGeo(armRadius * (rig.isKestrel ? 1.05 : 1.35),
      armRadius * 0.16, 5, 10, bag), rig.isKestrel ? m.accent : m.trim,
    0, -rig.foreArm * (0.25 + i * 0.14), 0);
    ring.rotation.x = Math.PI / 2;
  }
  const hand = buildHand(elbow, rig, m, bag, side);
  hand.position.y = -rig.foreArm;
  return {
    shoulder,
    elbow,
    hand,
    cap: shoulderCap,
    capBaseX: shoulderCap.scale.x,
    capBaseY: shoulderCap.scale.y,
    capBaseZ: shoulderCap.scale.z,
    capRestY: shoulderCap.position.y,
  };
}

function buildHand(parent, rig, m, bag, side) {
  const h = rig.h;
  const hand = new THREE.Group();
  hand.name = side > 0 ? 'handL' : 'handR';
  parent.add(hand);
  const palm = mesh(hand, shapedPalmGeo(h * 0.047, side, 9, 7, bag),
    rig.isKestrel ? m.leather : m.skin, 0, -h * 0.028, 0);
  palm.scale.set(rig.isKestrel ? 0.76 : 0.94, 0.92, rig.isKestrel ? 1.04 : 1.17);
  if (rig.isKestrel) {
    const gloveBack = mesh(hand, boxGeo(h * 0.026, h * 0.054, h * 0.092, bag),
      m.cloth2, -h * 0.03, -h * 0.025, 0);
    gloveBack.rotation.z = 0.08;
  }

  // Every finger has two articulated phalanges. The previous single dangling capsules technically
  // satisfied the part count but read as wires at gameplay distance and could never form a fist.
  const fingers = [];
  for (let i = 0; i < 4; i++) {
    const z = (i - 1.5) * h * 0.021;
    const lengthScale = FINGER_LENGTH_SCALES[i];
    const proximal = new THREE.Group();
    proximal.position.set(h * 0.018, -h * 0.065, z);
    hand.add(proximal);
    const firstLength = h * (rig.isKestrel ? 0.032 : 0.038) * lengthScale;
    mesh(proximal, profiledRadialGeo(
      firstLength,
      h * (rig.isKestrel ? 0.008 : 0.0105),
      FINGER_PROXIMAL_PROFILE,
      6,
      false,
      bag,
    ), rig.isKestrel ? m.skinLight : m.skin);
    const distal = new THREE.Group();
    distal.position.y = -firstLength;
    proximal.add(distal);
    const secondLength = h * (rig.isKestrel ? 0.027 : 0.032) * lengthScale;
    mesh(distal, profiledRadialGeo(
      secondLength,
      h * (rig.isKestrel ? 0.0075 : 0.0095),
      FINGER_DISTAL_PROFILE,
      6,
      false,
      bag,
    ), rig.isKestrel ? m.skinLight : m.skin);
    fingers.push({ proximal, distal });
  }
  const thumbRoot = new THREE.Group();
  thumbRoot.position.set(h * 0.012, -h * 0.035, side * h * 0.052);
  hand.add(thumbRoot);
  const thumb = mesh(thumbRoot, profiledRadialGeo(
    h * 0.046,
    h * 0.011,
    THUMB_PROFILE,
    6,
    false,
    bag,
  ), m.skinLight);
  thumb.rotation.x = side * 0.16;
  hand.userData.fingers = fingers;
  hand.userData.thumb = thumbRoot;
  return hand;
}

function buildLeg(body, rig, m, bag, side) {
  const h = rig.h;
  const hip = new THREE.Group();
  hip.name = side > 0 ? 'hipL' : 'hipR';
  hip.position.set(0, 0, side * rig.hip);
  body.add(hip);
  const thighR = rig.isKestrel ? h * 0.047 : h * 0.064;
  const hipCap = mesh(
    hip,
    sphereGeo(thighR * 1.08, rig.isKestrel ? 9 : 11, 8, bag),
    m.cloth,
    0,
    -thighR * 0.22,
    0,
  );
  hipCap.scale.set(
    rig.isKestrel ? 1.0 : 1.05,
    rig.isKestrel ? 0.8 : 0.78,
    rig.isKestrel ? 1.05 : 1.08,
  );
  mesh(hip, profiledRadialGeo(
    rig.upperLeg,
    thighR,
    THIGH_PROFILE,
    rig.isKestrel ? 9 : 10,
    false,
    bag,
  ), m.cloth);
  const knee = new THREE.Group();
  knee.name = side > 0 ? 'kneeL' : 'kneeR';
  knee.position.y = -rig.upperLeg;
  hip.add(knee);
  mesh(knee, sphereGeo(thighR * 1.08, 9, 7, bag), rig.isKestrel ? m.trim : m.metal, 0, 0, 0);
  mesh(knee, profiledRadialGeo(
    rig.lowerLeg,
    thighR * 0.82,
    CALF_PROFILE,
    rig.isKestrel ? 8 : 10,
    false,
    bag,
  ), rig.isKestrel ? m.cloth2 : m.cloth);
  const foot = new THREE.Group();
  foot.name = side > 0 ? 'footL' : 'footR';
  foot.position.y = -rig.lowerLeg;
  knee.add(foot);
  const boot = mesh(foot, capsuleGeo(h * (rig.isKestrel ? 0.044 : 0.055),
    h * (rig.isKestrel ? 0.16 : 0.18), rig.isKestrel ? 8 : 10, bag), m.leather,
  h * 0.055, -h * 0.018, 0);
  boot.rotation.z = -Math.PI / 2;
  boot.scale.z = rig.isKestrel ? 0.82 : 1.02;
  // A sloped vamp connects the rounded boot body to a readable forefoot without the detached
  // spherical "clown toe" silhouette exposed by the close-review capture.
  mesh(foot, wedgeGeo(
    h * (rig.isKestrel ? 0.078 : 0.09),
    h * (rig.isKestrel ? 0.042 : 0.052),
    h * (rig.isKestrel ? 0.078 : 0.1),
    bag,
  ), m.leather, h * (rig.isKestrel ? 0.116 : 0.128), -h * 0.033, 0);
  mesh(foot, footSoleGeo(
    h * 0.16,
    h * 0.018,
    h * (rig.isKestrel ? 0.095 : 0.125),
    bag,
  ),
    m.sole, h * 0.052, -h * 0.055, 0);
  const ankle = mesh(foot, torusGeo(h * (rig.isKestrel ? 0.05 : 0.064),
    h * 0.009, 6, 10, bag), rig.isKestrel ? m.accent : m.metal,
  0, h * 0.01, 0);
  ankle.rotation.x = Math.PI / 2;
  if (rig.isKestrel) {
    mesh(foot, boxGeo(h * 0.02, h * 0.035, h * 0.1, bag),
      m.accent, h * 0.112, -h * 0.012, 0);
  }
  return {
    hip,
    knee,
    foot,
    cap: hipCap,
    capBaseX: hipCap.scale.x,
    capBaseY: hipCap.scale.y,
    capBaseZ: hipCap.scale.z,
    capRestY: hipCap.position.y,
  };
}

function buildSash(body, rig, m, bag) {
  const sash = new THREE.Group();
  sash.name = 'sash';
  sash.position.set(-0.02, 0.05, -0.2);
  body.add(sash);
  rig.accessory = [];
  let parent = sash;
  for (let i = 0; i < 5; i++) {
    const joint = new THREE.Group();
    joint.position.y = i === 0 ? 0 : -0.11;
    parent.add(joint);
    const seg = mesh(joint, boxGeo(0.055, 0.14, 0.18 - i * 0.018, bag),
      i % 2 ? m.cloth2 : m.accent, -0.015, -0.07, 0);
    seg.castShadow = true;
    rig.accessory.push(joint);
    parent = joint;
  }
}

function buildBelt(body, rig, m, bag) {
  const belt = mesh(body, torusGeo(0.29, 0.035, 7, 20, bag), m.leather, 0, 0.08, 0);
  belt.rotation.x = Math.PI / 2;
  belt.scale.z = 0.9;
  mesh(body, boxGeo(0.08, 0.12, 0.16, bag), m.accent, 0.29, 0.08, 0);
  for (const side of [-1, 1]) {
    mesh(body, layeredPanelGeo(0.055, 0.22, 0.17, 0.7, 0.1, bag),
      m.metal, -0.01, -0.1, side * 0.26);
  }
  rig.accessory = [];
}

/**
 * Poses one rig directly from current combat state. Every authored channel is assigned each
 * frame—there is no asymptotic residual—so capture order cannot contaminate later screenshots.
 */
export function poseFighterVisual(
  group,
  f,
  alpha,
  simSeconds,
  opponent = null,
  motion = null,
  stateMotion = null,
  throwMotion = null,
) {
  const r = group.userData.rig;
  if (!r || !f) return;
  const worldX = lerp(f.prevX, f.x, alpha);
  group.position.set(
    worldX,
    lerp(f.prevY, f.y, alpha),
    lerp(f.prevZ, f.z, alpha),
  );
  let d = f.yaw - f.prevYaw;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  group.rotation.set(0, f.prevYaw + d * alpha, 0);

  resetPose(r);
  const pulse = Math.sin(simSeconds * (r.isKestrel ? 2.4 : 1.65));
  r.body.position.y = r.pelvisY + pulse * (r.isKestrel ? 0.009 : 0.006);
  r.chest.scale.y *= 1 + pulse * 0.008;

  applyGroundedLocomotion(r, f, worldX);
  applySidestepPose(r, f);
  applyCrouchPose(r, f);

  if (f.posture === 'air') {
    r.body.rotation.z = -0.28;
    r.legs.left.hip.rotation.z = -0.35;
    r.legs.right.hip.rotation.z = 0.48;
    r.legs.left.knee.rotation.z = 0.65;
  }

  const guardAmount = guardBlend(f);
  if (guardAmount > 0) guardPose(r, guardAmount);
  if (f.state === 'round-intro') {
    r.isKestrel ? kestrelIntro(r, simSeconds) : bulwarkIntro(r, simSeconds);
  }
  const paired = !!(throwMotion && opponent && f.throwPartner >= 0
    && opponent.throwPartner === f.index);
  if (f.move && !paired) applyMovePose(r, f, motion);
  if (paired && f.throwMoveId) {
    applyPairedThrowAttacker(r, f, opponent, throwMotion);
  } else if (paired) {
    applyPairedThrowVictim(r, f, opponent, throwMotion);
  } else if (f.move && f.move.isThrow && opponent) {
    applyThrowGrip(r, f, opponent);
  }
  if (stateMotion) applyStateMotion(r, f, stateMotion);
  if (!paired && throwMotion && f.prevState === 'throw-execute' && f.stateTick <= 60) {
    applyThrowReleaseAttacker(r, f, throwMotion);
  }

  applyJointVolumePose(r);
  applyFacialExpression(r, f);
  if (r.isKestrel && r.accessory.length) {
    applyAccessoryPose(r, f, paired, opponent, throwMotion);
  }
}

function resetPose(r) {
  r.body.position.set(0, r.pelvisY, 0);
  r.body.rotation.set(0, 0, r.isKestrel ? 0.055 : 0.025);
  r.head.rotation.set(0, 0, 0);
  r.chest.scale.y = 1;
  r.abdomen.scale.y = 1;
  for (let i = 0; i < r.brows.length; i++) {
    const brow = r.brows[i];
    brow.rotation.x = brow.userData.restRotationX;
    brow.position.y = brow.userData.restY;
  }
  if (r.jaw) {
    r.jaw.rotation.z = r.jaw.userData.restRotationZ;
    r.jaw.position.y = r.jaw.userData.restY;
  }
  const arms = [r.arms.left, r.arms.right];
  for (let i = 0; i < arms.length; i++) {
    const arm = arms[i];
    const near = i === 0;
    arm.shoulder.rotation.set(0, 0,
      r.isKestrel ? (near ? 0.82 : 0.62) : (near ? 0.56 : 0.42));
    arm.elbow.rotation.set(0, 0,
      r.isKestrel ? (near ? 0.62 : 0.74) : (near ? 0.72 : 0.64));
    arm.hand.rotation.set(0, 0, 0);
    setHandCurl(arm.hand, r.isKestrel ? 0.78 : 0.84, near ? 0.06 : -0.04);
  }
  r.arms.left.shoulder.rotation.x = r.isKestrel ? -0.15 : -0.24;
  r.arms.right.shoulder.rotation.x = r.isKestrel ? 0.18 : 0.25;
  for (const leg of [r.legs.left, r.legs.right]) {
    leg.hip.rotation.set(0, 0, r.isKestrel ? 0.055 : -0.02);
    leg.knee.rotation.set(0, 0, r.isKestrel ? 0.06 : 0.11);
    leg.foot.rotation.set(0, 0, 0);
  }
  r.legs.left.hip.rotation.x = r.isKestrel ? -0.1 : -0.2;
  r.legs.right.hip.rotation.x = r.isKestrel ? 0.12 : 0.2;
}

function applyJointVolumePose(r) {
  for (const arm of [r.arms.left, r.arms.right]) {
    const lift = Math.abs(Math.sin(arm.shoulder.rotation.z));
    const depth = Math.abs(Math.sin(arm.shoulder.rotation.x));
    arm.cap.scale.set(
      arm.capBaseX * (1 + lift * 0.12),
      arm.capBaseY * (1 - lift * 0.09),
      arm.capBaseZ * (1 + depth * 0.1),
    );
    arm.cap.position.y = arm.capRestY * (1 - lift * 0.16);
  }
  for (const leg of [r.legs.left, r.legs.right]) {
    const fold = Math.abs(Math.sin(leg.hip.rotation.z));
    const depth = Math.abs(Math.sin(leg.hip.rotation.x));
    leg.cap.scale.set(
      leg.capBaseX * (1 + fold * 0.08),
      leg.capBaseY * (1 - fold * 0.12),
      leg.capBaseZ * (1 + depth * 0.1),
    );
    leg.cap.position.y = leg.capRestY * (1 - fold * 0.12);
  }
}

function applyFacialExpression(r, f) {
  const hurt = f.inHitstun || f.state === 'knockdown-fall'
    || f.state === 'knockdown-ground' || f.state === 'match-loss';
  const throwStrain = f.throwPartner >= 0;
  const attack = !!f.move;
  const guard = f.guarding || f.state === 'parry-active' || f.state === 'parry-success';
  const tension = clamp((attack ? 0.62 : 0) + (guard ? 0.34 : 0) + (throwStrain ? 0.28 : 0), 0, 1);
  const pain = hurt ? clamp(0.38 + Math.abs(f.vx) * 0.05 + Math.abs(f.vy) * 0.035, 0, 0.9) : 0;
  for (let i = 0; i < r.brows.length; i++) {
    const brow = r.brows[i];
    const side = brow.userData.expressionSide;
    brow.rotation.x = brow.userData.restRotationX + side * (tension * 0.16 - pain * 0.24);
    brow.position.y = brow.userData.restY - r.h * pain * 0.004;
  }
  if (r.jaw) {
    r.jaw.rotation.z = r.jaw.userData.restRotationZ + pain * 0.11 + tension * 0.025;
    r.jaw.position.y = r.jaw.userData.restY - r.h * pain * 0.006;
  }
}

function applyAccessoryPose(r, f, paired, opponent, throwMotion) {
  const seconds = f.poseTick * FIXED_STEP;
  const localVelocity = f.vx * f.facing;
  let trail = clamp(-localVelocity * 0.055, -0.24, 0.28);
  let throwSweep = 0;
  let clearance = 0;
  if (paired && !f.throwMoveId && opponent && throwMotion) {
    const phase = throwPhase(THROW_PHASE, opponent, throwMotion);
    throwSweep = clamp(-throwMotion.victimRoll * phase.turn * 0.2 - phase.lift * 0.16, -0.38, 0.34);
    clearance = phase.lift * 0.16 + phase.impact * 0.1;
  } else if (f.state === 'knockdown-fall' || f.posture === 'air') {
    throwSweep = clamp(-r.body.rotation.z * 0.08 - Math.abs(f.vy) * 0.025, -0.28, 0.08);
    clearance = 0.08;
  }
  for (let i = 0; i < r.accessory.length; i++) {
    const lag = (i + 1) / r.accessory.length;
    const sway = Math.sin(seconds * 4.4 - i * 0.7) * (0.045 + i * 0.014);
    r.accessory[i].rotation.z = -0.14 + trail * lag + throwSweep * lag - sway;
    r.accessory[i].rotation.x = -clearance * lag
      + Math.sin(seconds * 3.1 + i) * 0.045 * (1 - clearance);
  }
}

/**
 * A travel-driven gait keeps stance feet coupled to authoritative root movement. During the
 * planted part of each cycle, local foot travel exactly counters root travel; the other foot uses
 * a lifted eased swing. No presentation history or wall clock is needed.
 */
function applyGroundedLocomotion(r, f, worldX) {
  const active = LOCOMOTION_STATES.has(f.state);
  const outgoing = !active && LOCOMOTION_STATES.has(f.prevState) && f.stateTick < 8;
  if (!active && !outgoing) return;

  const locomotionState = active ? f.state : f.prevState;
  const enteringFromMotion = LOCOMOTION_STATES.has(f.prevState);
  const amount = active
    ? (enteringFromMotion ? 1 : smooth(f.stateTick / 7))
    : 1 - smooth(f.stateTick / 8);
  const dash = locomotionState.includes('dash');
  const backward = locomotionState === 'walk-back' || locomotionState === 'dash-back';
  const strideLength = r.isKestrel
    ? (dash ? 0.84 : 0.58)
    : (dash ? 0.76 : 0.52);
  const stanceFraction = dash ? 0.56 : 0.62;
  const lift = r.h * (dash ? 0.085 : 0.048);
  const localTravel = worldX * f.facing;
  const cycle = localTravel / strideLength;
  const bounce = Math.abs(Math.sin(cycle * Math.PI * 2)) * r.h * (dash ? 0.018 : 0.009);

  r.body.position.y += bounce * amount;
  r.body.rotation.z += (backward ? -0.1 : dash ? 0.15 : 0.09) * amount;
  r.body.rotation.y += Math.sin(cycle * Math.PI * 2) * 0.035 * amount;

  poseGaitLeg(r, r.legs.left, cycle, strideLength, stanceFraction, lift, amount);
  poseGaitLeg(r, r.legs.right, cycle + 0.5, strideLength, stanceFraction, lift, amount);

  const counter = Math.sin(cycle * Math.PI * 2) * (dash ? 0.36 : 0.23) * amount;
  r.arms.left.shoulder.rotation.z -= counter;
  r.arms.right.shoulder.rotation.z += counter;
  r.arms.left.elbow.rotation.z += Math.max(0, counter) * 0.22;
  r.arms.right.elbow.rotation.z += Math.max(0, -counter) * 0.22;
}

function poseGaitLeg(r, leg, cycle, strideLength, stanceFraction, lift, amount) {
  sampleGaitTarget(GAIT_TARGET, cycle, strideLength, stanceFraction, lift);
  solveLegTarget(r, leg, GAIT_TARGET.x, r.restAnkleY + GAIT_TARGET.y, amount);
}

function applySidestepPose(r, f) {
  const active = f.state === 'sidestep-left' || f.state === 'sidestep-right';
  const recovering = f.state === 'sidestep-recovery' && f.stateTick < 8;
  if (!active && !recovering) return;
  const direction = (active ? f.state : f.prevState) === 'sidestep-left' ? 1 : -1;
  const amount = active ? smooth(f.stateTick / 5) : 1 - smooth(f.stateTick / 8);
  r.body.position.y -= r.h * 0.035 * amount;
  r.body.rotation.x += direction * 0.13 * amount;
  r.body.rotation.z += 0.08 * amount;
  r.legs.left.hip.rotation.x -= direction * 0.28 * amount;
  r.legs.right.hip.rotation.x -= direction * 0.2 * amount;
  r.legs.left.knee.rotation.z += 0.32 * amount;
  r.legs.right.knee.rotation.z += 0.24 * amount;
  r.arms.left.shoulder.rotation.x += direction * 0.2 * amount;
  r.arms.right.shoulder.rotation.x += direction * 0.16 * amount;
  plantFoot(r, r.legs.left);
  plantFoot(r, r.legs.right);
}

function crouchBlend(f) {
  const transitionTicks = Math.max(1, (f.stats.crouchEnterFrames || 6) * 2);
  if (f.state === 'crouch-enter') return smooth(f.stateTick / transitionTicks);
  if (f.state === 'crouch-exit') return 1 - smooth(f.stateTick / transitionTicks);
  return f.posture === 'crouch' ? 1 : 0;
}

function applyCrouchPose(r, f) {
  const amount = crouchBlend(f);
  if (amount <= 0) return;
  r.body.position.y = lerp(r.body.position.y, r.pelvisY * 0.76, amount);
  r.body.rotation.z = lerp(r.body.rotation.z, 0.17, amount);
  solveLegTarget(r, r.legs.left, r.h * 0.075, r.restAnkleY, amount);
  solveLegTarget(r, r.legs.right, -r.h * 0.055, r.restAnkleY, amount);
}

function guardBlend(f) {
  if (f.state === 'guard-stand' || f.state === 'guard-crouch') {
    return smooth(f.stateTick / 8);
  }
  if (f.state === 'guard-recovery') return 1 - smooth(f.stateTick / 7);
  return f.guarding ? 1 : 0;
}

function guardPose(r, amount) {
  r.body.rotation.z = lerp(r.body.rotation.z, 0.08, amount);
  r.arms.left.shoulder.rotation.z = lerp(
    r.arms.left.shoulder.rotation.z, r.isKestrel ? 1.28 : 0.92, amount,
  );
  r.arms.left.elbow.rotation.z = lerp(
    r.arms.left.elbow.rotation.z, r.isKestrel ? 0.58 : 0.78, amount,
  );
  r.arms.right.shoulder.rotation.z = lerp(
    r.arms.right.shoulder.rotation.z, r.isKestrel ? 0.86 : 1.08, amount,
  );
  r.arms.right.elbow.rotation.z = lerp(
    r.arms.right.elbow.rotation.z, r.isKestrel ? 0.82 : 0.62, amount,
  );
  setHandCurl(r.arms.left.hand, lerp(0.78, 0.9, amount), 0.02);
  setHandCurl(r.arms.right.hand, lerp(0.78, 0.9, amount), -0.02);
  r.head.rotation.z = lerp(r.head.rotation.z, -0.08, amount);
}

function applyStateMotion(r, f, motion) {
  switch (motion.family) {
    case 'hit-stand':
      applyStandingHit(r, f, motion);
      break;
    case 'hit-crouch':
      applyCrouchingHit(r, f, motion);
      break;
    case 'hit-launch':
    case 'hit-air':
      applyAirHit(r, f, motion);
      break;
    case 'hit-wall':
    case 'wall-splat':
      applyWallImpact(r, f, motion);
      break;
    case 'wall-slump':
      applyWallSlump(r, f, motion);
      break;
    case 'knockdown-fall':
      applyKnockdownFall(r, f, motion);
      break;
    case 'knockdown-ground':
      applyGroundPose(r, f, motion);
      break;
    case 'wakeup-neutral':
      applyWakeupNeutral(r, f, motion);
      break;
    case 'wakeup-roll':
      applyWakeupRoll(r, f, motion);
      break;
    case 'wakeup-attack':
      applyWakeupAttack(r, f, motion);
      break;
    case 'throw-break':
    case 'throw-tech':
      applyThrowBreakPose(r, f, motion);
      break;
    case 'landing':
    case 'hard-landing':
      applyLandingPose(r, f, motion);
      break;
    case 'victory':
      applyVictoryPose(r, f, motion);
      break;
    case 'defeat':
      applyDefeatPose(r, f, motion);
      break;
    default:
      break;
  }
}

function reactionAmount(f, motion) {
  return motion.intensity * (1 - smooth(f.stateTick / Math.max(1, motion.recoverTicks)));
}

function applyStandingHit(r, f, motion) {
  const amount = reactionAmount(f, motion);
  const back = f.vx * f.facing > 0 ? 1 : -1;
  r.body.rotation.z += Math.abs(motion.lean) * back * amount;
  r.body.rotation.x += motion.twist * amount;
  r.body.position.y -= r.h * 0.025 * amount;
  r.head.rotation.z -= back * 0.34 * amount;
  r.head.rotation.y += motion.twist * 0.42 * amount;
  r.arms.left.shoulder.rotation.z = lerp(r.arms.left.shoulder.rotation.z, -0.34, amount);
  r.arms.right.shoulder.rotation.z = lerp(r.arms.right.shoulder.rotation.z, 0.18, amount);
  r.arms.left.elbow.rotation.z = lerp(r.arms.left.elbow.rotation.z, 0.28, amount);
  r.arms.right.elbow.rotation.z = lerp(r.arms.right.elbow.rotation.z, 0.2, amount);
  r.legs.left.knee.rotation.z += 0.24 * amount;
  r.legs.right.knee.rotation.z += 0.14 * amount;
  setHandCurl(r.arms.left.hand, 0.24, 0.12);
  setHandCurl(r.arms.right.hand, 0.34, -0.1);
  groundBodyToFeet(r, amount);
}

function applyCrouchingHit(r, f, motion) {
  const amount = reactionAmount(f, motion);
  r.body.position.y -= r.h * 0.05 * amount;
  r.body.rotation.z += motion.lean * amount;
  r.body.rotation.x += 0.18 * amount;
  r.head.rotation.z -= motion.lean * 0.75 * amount;
  r.arms.left.shoulder.rotation.z = lerp(r.arms.left.shoulder.rotation.z, 1.7, amount);
  r.arms.right.shoulder.rotation.z = lerp(r.arms.right.shoulder.rotation.z, 0.2, amount);
  r.legs.left.knee.rotation.z += 0.42 * amount;
  r.legs.right.knee.rotation.z += 0.34 * amount;
  groundBodyToFeet(r, amount);
  void f;
}

function applyAirHit(r, f, motion) {
  const entry = smooth(f.stateTick / Math.max(1, motion.enterTicks));
  const spin = clamp(Math.abs(f.vx) * 0.055 + Math.abs(f.vy) * 0.035, 0.16, 0.62);
  const direction = f.vx * f.facing > 0 ? 1 : -1;
  r.body.rotation.z = motion.lean * entry + direction * spin * entry;
  r.body.rotation.x = motion.twist * entry;
  r.head.rotation.z = -direction * 0.28 * entry;
  r.arms.left.shoulder.rotation.z = lerp(0.82, -0.42, entry);
  r.arms.right.shoulder.rotation.z = lerp(0.62, 1.72, entry);
  r.arms.left.elbow.rotation.z = 0.24 + entry * 0.18;
  r.arms.right.elbow.rotation.z = 0.3 + entry * 0.24;
  r.legs.left.hip.rotation.z = lerp(0.05, -0.58, entry);
  r.legs.right.hip.rotation.z = lerp(-0.02, 0.72, entry);
  r.legs.left.knee.rotation.z = lerp(0.06, 1.08, entry);
  r.legs.right.knee.rotation.z = lerp(0.11, 0.46, entry);
}

function applyWallImpact(r, f, motion) {
  const impact = motion.family === 'wall-splat'
    ? motion.intensity
    : reactionAmount(f, motion);
  r.body.position.y -= r.h * motion.compression * impact;
  r.body.rotation.z = motion.lean * impact;
  r.body.rotation.x = (f.wallSide || 1) * 0.12 * impact;
  r.head.rotation.z = -0.24 * impact;
  r.arms.left.shoulder.rotation.z = lerp(r.arms.left.shoulder.rotation.z, 2.08, impact);
  r.arms.right.shoulder.rotation.z = lerp(r.arms.right.shoulder.rotation.z, -0.64, impact);
  r.arms.left.elbow.rotation.z = 0.26;
  r.arms.right.elbow.rotation.z = 0.34;
  r.legs.left.knee.rotation.z += 0.28 * impact;
  r.legs.right.knee.rotation.z += 0.4 * impact;
  setHandCurl(r.arms.left.hand, 0.22, 0.16);
  setHandCurl(r.arms.right.hand, 0.22, -0.16);
  groundBodyToFeet(r, clamp(impact, 0, 1));
}

function applyWallSlump(r, f, motion) {
  const amount = smooth(f.stateTick / Math.max(1, motion.enterTicks));
  r.body.position.y = lerp(r.pelvisY, r.pelvisY * 0.42, amount);
  r.body.rotation.z = lerp(0.02, 0.54, amount);
  r.body.rotation.x = lerp(0, (f.wallSide || 1) * 0.18, amount);
  r.head.rotation.z = 0.38 * amount;
  r.arms.left.shoulder.rotation.z = lerp(0.82, -0.16, amount);
  r.arms.right.shoulder.rotation.z = lerp(0.62, 0.04, amount);
  r.legs.left.hip.rotation.z = lerp(0.05, -0.72, amount);
  r.legs.right.hip.rotation.z = lerp(-0.02, -0.5, amount);
  r.legs.left.knee.rotation.z = lerp(0.06, 1.38, amount);
  r.legs.right.knee.rotation.z = lerp(0.11, 1.14, amount);
  plantFoot(r, r.legs.left);
  plantFoot(r, r.legs.right);
}

function applyKnockdownFall(r, f, motion) {
  const amount = smooth(f.stateTick / Math.max(1, motion.enterTicks));
  const hard = f.knockdownHard ? 1.16 : 0.92;
  r.body.rotation.z = motion.lean * amount * hard;
  r.body.rotation.x = motion.twist * amount;
  r.head.rotation.z = -motion.lean * 0.24 * amount;
  r.arms.left.shoulder.rotation.z = lerp(0.82, -0.52, amount);
  r.arms.right.shoulder.rotation.z = lerp(0.62, 1.86, amount);
  r.arms.left.shoulder.rotation.x += 0.36 * amount;
  r.arms.right.shoulder.rotation.x -= 0.3 * amount;
  r.legs.left.hip.rotation.z = lerp(0.05, -0.72, amount);
  r.legs.right.hip.rotation.z = lerp(-0.02, 0.52, amount);
  r.legs.left.knee.rotation.z = lerp(0.06, 0.94, amount);
  r.legs.right.knee.rotation.z = lerp(0.11, 0.42, amount);
}

function applyGroundPose(r, f, motion) {
  const amount = smooth(f.stateTick / Math.max(1, motion.enterTicks));
  r.body.position.y = lerp(r.pelvisY, r.h * (f.knockdownHard ? 0.095 : 0.11), amount);
  r.body.rotation.z = lerp(r.isKestrel ? 0.055 : 0.025, motion.lean, amount);
  r.body.rotation.x = motion.twist * amount;
  r.head.rotation.z = -motion.lean * 0.16 * amount;
  r.head.rotation.x = (f.faceUp ? -0.08 : 0.26) * amount;
  r.arms.left.shoulder.rotation.z = lerp(0.82, -0.38, amount);
  r.arms.right.shoulder.rotation.z = lerp(0.62, 1.48, amount);
  r.arms.left.shoulder.rotation.x = 0.48 * amount;
  r.arms.right.shoulder.rotation.x = -0.42 * amount;
  r.legs.left.hip.rotation.z = lerp(0.05, -0.34, amount);
  r.legs.right.hip.rotation.z = lerp(-0.02, 0.42, amount);
  r.legs.left.hip.rotation.x = -0.34 * amount;
  r.legs.right.hip.rotation.x = 0.3 * amount;
  r.legs.left.knee.rotation.z = lerp(0.06, 0.52, amount);
  r.legs.right.knee.rotation.z = lerp(0.11, 0.72, amount);
}

function applyWakeupNeutral(r, f, motion) {
  const amount = smooth(f.stateTick / Math.max(1, motion.enterTicks));
  const lift = smooth(clamp((amount - 0.12) / 0.88, 0, 1));
  r.body.position.y = lerp(r.h * 0.11, r.pelvisY, lift);
  r.body.rotation.z = lerp(-1.42, r.isKestrel ? 0.055 : 0.025, lift);
  r.body.rotation.x = lerp(f.faceUp ? -0.18 : 2.7, 0, lift);
  r.head.rotation.z = lerp(0.26, -0.05, lift);
  r.arms.left.shoulder.rotation.z = lerp(-0.48, 1.08, lift);
  r.arms.right.shoulder.rotation.z = lerp(1.46, 0.78, lift);
  r.legs.left.hip.rotation.z = lerp(-0.38, -0.5, 1 - Math.abs(lift * 2 - 1));
  r.legs.right.hip.rotation.z = lerp(0.42, -0.42, 1 - Math.abs(lift * 2 - 1));
  r.legs.left.knee.rotation.z = lerp(0.56, 1.2, 1 - Math.abs(lift * 2 - 1));
  r.legs.right.knee.rotation.z = lerp(0.68, 1.08, 1 - Math.abs(lift * 2 - 1));
  if (lift > 0.62) {
    solveLegTarget(r, r.legs.left, r.h * 0.06, r.restAnkleY, smooth((lift - 0.62) / 0.38));
    solveLegTarget(r, r.legs.right, -r.h * 0.05, r.restAnkleY, smooth((lift - 0.62) / 0.38));
  }
}

function applyWakeupRoll(r, f, motion) {
  const amount = smooth(f.stateTick / Math.max(1, motion.enterTicks));
  const rise = smooth(clamp((amount - 0.62) / 0.38, 0, 1));
  r.body.position.y = lerp(r.h * 0.14, r.pelvisY, rise);
  r.body.rotation.x = (f.faceUp ? -0.2 : 2.7) + amount * Math.PI * 2;
  r.body.rotation.z = lerp(-1.28, r.isKestrel ? 0.055 : 0.025, rise);
  r.head.rotation.z = -0.18 * (1 - rise);
  r.arms.left.shoulder.rotation.z = lerp(-0.2, 1.08, rise);
  r.arms.right.shoulder.rotation.z = lerp(1.36, 0.82, rise);
  r.legs.left.hip.rotation.z = lerp(-0.78, 0.05, rise);
  r.legs.right.hip.rotation.z = lerp(-0.58, -0.02, rise);
  r.legs.left.knee.rotation.z = lerp(1.42, 0.06, rise);
  r.legs.right.knee.rotation.z = lerp(1.3, 0.11, rise);
}

function applyWakeupAttack(r, f, motion) {
  const grounded = 1 - smooth(f.stateTick / Math.max(1, motion.enterTicks));
  r.body.position.y -= r.h * motion.compression * grounded;
  r.body.rotation.z += 0.22 * grounded;
  r.legs.left.knee.rotation.z += 0.76 * grounded;
  r.legs.right.knee.rotation.z += 0.68 * grounded;
  r.arms.left.shoulder.rotation.z += 0.28 * grounded;
  r.arms.right.shoulder.rotation.z += 0.2 * grounded;
  groundBodyToFeet(r, grounded);
}

function applyThrowBreakPose(r, f, motion) {
  const amount = reactionAmount(f, motion);
  const attackerRejected = motion.family === 'throw-break';
  r.body.position.y -= r.h * motion.compression * amount;
  r.body.rotation.z += motion.lean * amount;
  r.body.rotation.x += (attackerRejected ? 0.14 : -0.1) * amount;
  r.head.rotation.z -= motion.lean * 0.42 * amount;
  r.arms.left.shoulder.rotation.z = lerp(
    r.arms.left.shoulder.rotation.z,
    attackerRejected ? -0.46 : 1.32,
    amount,
  );
  r.arms.right.shoulder.rotation.z = lerp(
    r.arms.right.shoulder.rotation.z,
    attackerRejected ? 1.74 : 1.16,
    amount,
  );
  r.arms.left.elbow.rotation.z = lerp(r.arms.left.elbow.rotation.z, 0.26, amount);
  r.arms.right.elbow.rotation.z = lerp(r.arms.right.elbow.rotation.z, 0.32, amount);
  r.legs.left.knee.rotation.z += (attackerRejected ? 0.28 : 0.48) * amount;
  r.legs.right.knee.rotation.z += (attackerRejected ? 0.42 : 0.32) * amount;
  setHandCurl(r.arms.left.hand, attackerRejected ? 0.2 : 0.82, 0.14);
  setHandCurl(r.arms.right.hand, attackerRejected ? 0.2 : 0.82, -0.14);
  groundBodyToFeet(r, amount);
}

function applyLandingPose(r, f, motion) {
  const amount = reactionAmount(f, motion);
  r.body.position.y -= r.h * motion.compression * amount;
  r.body.rotation.z += (motion.family === 'hard-landing' ? 0.3 : 0.16) * amount;
  r.head.rotation.z -= 0.12 * amount;
  solveLegTarget(r, r.legs.left, r.h * 0.08, r.restAnkleY, amount);
  solveLegTarget(r, r.legs.right, -r.h * 0.06, r.restAnkleY, amount);
  r.arms.left.shoulder.rotation.z -= 0.36 * amount;
  r.arms.right.shoulder.rotation.z -= 0.22 * amount;
}

function applyVictoryPose(r, f, motion) {
  const amount = smooth(f.stateTick / Math.max(1, motion.enterTicks));
  r.body.rotation.z = lerp(r.body.rotation.z, motion.lean, amount);
  r.body.rotation.y = motion.twist * amount;
  r.head.rotation.z = lerp(0, r.isKestrel ? -0.14 : 0.08, amount);
  if (r.isKestrel) {
    r.arms.left.shoulder.rotation.z = lerp(0.82, 1.12, amount);
    r.arms.left.elbow.rotation.z = lerp(0.62, 0.5, amount);
    r.arms.right.shoulder.rotation.z = lerp(0.62, 2.4, amount);
    r.arms.right.elbow.rotation.z = lerp(0.74, -0.18, amount);
    setHandCurl(r.arms.right.hand, lerp(0.78, 0.16, amount), -0.1);
  } else {
    r.arms.left.shoulder.rotation.z = lerp(0.56, 1.62, amount);
    r.arms.right.shoulder.rotation.z = lerp(0.42, 1.42, amount);
    r.arms.left.elbow.rotation.z = lerp(0.72, 1.18, amount);
    r.arms.right.elbow.rotation.z = lerp(0.64, 1.26, amount);
    setHandCurl(r.arms.left.hand, 0.96, 0);
    setHandCurl(r.arms.right.hand, 0.96, 0);
  }
  groundBodyToFeet(r, amount);
}

function applyDefeatPose(r, f, motion) {
  const amount = smooth(f.stateTick / Math.max(1, motion.enterTicks));
  if (motion.id === 'match-defeat') {
    r.body.position.y = lerp(r.pelvisY, r.h * 0.11, amount);
    r.body.rotation.z = lerp(0.02, motion.lean, amount);
    r.body.rotation.x = 0.26 * amount;
    r.legs.left.knee.rotation.z = lerp(0.06, 0.72, amount);
    r.legs.right.knee.rotation.z = lerp(0.11, 0.46, amount);
  } else {
    r.body.position.y -= r.h * motion.compression * amount;
    r.body.rotation.z = motion.lean * amount;
    r.legs.left.hip.rotation.z -= 0.52 * amount;
    r.legs.right.hip.rotation.z -= 0.34 * amount;
    r.legs.left.knee.rotation.z += 1.22 * amount;
    r.legs.right.knee.rotation.z += 0.94 * amount;
    groundBodyToFeet(r, amount);
  }
  r.head.rotation.z = 0.34 * amount;
  r.arms.left.shoulder.rotation.z = lerp(0.82, -0.2, amount);
  r.arms.right.shoulder.rotation.z = lerp(0.62, -0.08, amount);
  setHandCurl(r.arms.left.hand, 0.18, 0.12);
  setHandCurl(r.arms.right.hand, 0.18, -0.12);
}

function applyMovePose(r, f, motion) {
  const m = f.move;
  const tick = f.moveTick;
  if (!motion) return;

  let wind = 0;
  let contact = 0;
  let recovery = 0;
  if (tick < m.tStartup) {
    const t = tick / Math.max(1, m.tStartup);
    if (t < 0.64) {
      wind = smooth(t / 0.64);
    } else {
      contact = smooth((t - 0.64) / 0.36);
      wind = 1 - contact;
    }
  } else if (tick < m.tStartup + m.tActive) {
    const t = (tick - m.tStartup) / Math.max(1, m.tActive);
    contact = 1 + Math.sin(t * Math.PI) * motion.follow;
  } else {
    const t = (tick - m.tStartup - m.tActive) / Math.max(1, m.tRecovery);
    recovery = clamp(t, 0, 1);
    contact = 1 - smooth(t);
  }

  if (motion.family === 'stance') {
    applyStanceTransition(r, contact, wind);
    return;
  }
  if (motion.family === 'parry') {
    applyParryPose(r, contact, wind);
    return;
  }
  if (motion.family === 'grapple') {
    const reach = Math.max(contact, wind * 0.45);
    r.body.rotation.z += motion.lean * reach;
    r.arms.left.shoulder.rotation.z = lerp(r.arms.left.shoulder.rotation.z, 1.18, reach);
    r.arms.right.shoulder.rotation.z = lerp(r.arms.right.shoulder.rotation.z, 1.18, reach);
    r.arms.left.elbow.rotation.z = lerp(r.arms.left.elbow.rotation.z, 0.72, reach);
    r.arms.right.elbow.rotation.z = lerp(r.arms.right.elbow.rotation.z, 0.72, reach);
    setHandCurl(r.arms.left.hand, lerp(0.82, 0.28, reach), 0.16 * reach);
    setHandCurl(r.arms.right.hand, lerp(0.82, 0.28, reach), -0.16 * reach);
    return;
  }

  const joint = motion.joint;
  const left = motion.side === 'left';
  const arm = left ? r.arms.left : r.arms.right;
  const leg = left ? r.legs.left : r.legs.right;
  const sideSign = left ? 1 : -1;
  const action = Math.max(contact, wind * 0.55);

  r.body.position.y -= motion.crouch * r.h * 0.18 * wind;
  r.body.position.y += motion.lift * r.h * contact;
  r.body.rotation.z += motion.lean * (contact * 0.72 - wind * 0.32);
  r.body.rotation.y += sideSign * motion.twist * (contact + wind * 0.35);
  r.head.rotation.y -= sideSign * motion.twist * action * 0.28;
  r.head.rotation.z -= motion.lean * action * 0.18;

  if (joint.startsWith('foot') || joint.startsWith('knee')) {
    poseStrikingLeg(r, leg, joint, motion, wind, contact);
    const support = left ? r.legs.right : r.legs.left;
    plantFoot(r, support);
    groundBodyToLeg(r, support, clamp(action, 0, 1));
    // Counter-rotation keeps both hands guarding while the hips commit to the kick.
    r.arms.left.shoulder.rotation.z -= contact * motion.twist * 0.14;
    r.arms.right.shoulder.rotation.z += contact * motion.twist * 0.1;
    setHandCurl(r.arms.left.hand, 0.9, 0.02);
    setHandCurl(r.arms.right.hand, 0.9, -0.02);
  } else if (joint === 'torso') {
    r.body.rotation.z += motion.lean * action;
    r.body.position.x = motion.contactX * 0.16 * contact;
    r.arms.left.shoulder.rotation.z = lerp(r.arms.left.shoulder.rotation.z, 0.48, action);
    r.arms.right.shoulder.rotation.z = lerp(r.arms.right.shoulder.rotation.z, 0.48, action);
    r.legs.left.knee.rotation.z += action * 0.2;
    r.legs.right.knee.rotation.z += action * 0.2;
    plantFoot(r, r.legs.left);
    plantFoot(r, r.legs.right);
    groundBodyToFeet(r, clamp(action, 0, 1));
  } else {
    poseStrikingArm(r, arm, joint, motion, wind, contact, sideSign);
    setHandCurl(
      arm.hand,
      motion.handShape === 'open' ? 0.18 : motion.handShape === 'grip' ? 0.42 : 0.94,
      motion.handShape === 'open' ? sideSign * 0.08 : 0,
    );
    plantFoot(r, r.legs.left);
    plantFoot(r, r.legs.right);
    groundBodyToFeet(r, clamp(action, 0, 1));
  }

  if (recovery > 0) applyRecoveryAccent(r, motion, recovery, sideSign, arm);

  if (motion.family === 'finisher') {
    r.chest.scale.y *= 1 + contact * 0.08;
    r.body.position.x += contact * 0.08;
  }
}

function applyRecoveryAccent(r, motion, recovery, sideSign, actionArm) {
  const settle = Math.sin(recovery * Math.PI);
  const recoil = settle * motion.recoveryRecoil;
  const agile = motion.recoveryStyle === 'snap-guard';
  r.body.rotation.z += motion.lean * recoil * (agile ? -0.42 : 0.34);
  r.body.rotation.y -= sideSign * motion.recoveryTwist * settle;
  r.body.position.y -= r.h * motion.recoveryDip * settle;
  r.head.rotation.z += motion.lean * recoil * (agile ? 0.24 : -0.16);
  r.head.rotation.y += sideSign * motion.recoveryTwist * settle * 0.36;

  if (agile) {
    const guard = motion.recoveryGuard * settle;
    r.arms.left.shoulder.rotation.z = lerp(r.arms.left.shoulder.rotation.z, 1.18, guard);
    r.arms.right.shoulder.rotation.z = lerp(r.arms.right.shoulder.rotation.z, 0.9, guard);
    r.arms.left.shoulder.rotation.x -= guard * 0.16;
    r.arms.right.shoulder.rotation.x += guard * 0.14;
    r.arms.left.elbow.rotation.z = lerp(r.arms.left.elbow.rotation.z, 0.58, guard);
    r.arms.right.elbow.rotation.z = lerp(r.arms.right.elbow.rotation.z, 0.74, guard);
    setHandCurl(r.arms.left.hand, lerp(0.82, 0.9, guard), 0.02);
    setHandCurl(r.arms.right.hand, lerp(0.82, 0.9, guard), -0.02);
  } else {
    const drag = recoil * 0.42;
    actionArm.shoulder.rotation.z += sideSign * drag;
    actionArm.elbow.rotation.z += recoil * 0.28;
    r.chest.scale.y *= 1 + recoil * 0.04;
    r.legs.left.knee.rotation.z += settle * 0.12;
    r.legs.right.knee.rotation.z += settle * 0.09;
    groundBodyToFeet(r, settle * 0.5);
  }
}

function poseStrikingArm(r, arm, joint, motion, wind, contact, sideSign) {
  const baseUpper = arm.shoulder.rotation.z;
  const baseLower = arm.elbow.rotation.z;
  let windUpper = baseUpper - motion.windup;
  let windLower = baseLower + motion.windup * 0.72;
  if (motion.arc === 'overhead') {
    windUpper = 2.28;
    windLower = 0.38;
  } else if (motion.arc === 'rising' || motion.arc === 'finisher') {
    windUpper = -0.18;
    windLower = 1.12;
  } else if (motion.arc === 'round') {
    windUpper = -0.26;
    windLower = 0.94;
  } else if (motion.arc === 'compact') {
    windUpper = 0.18;
    windLower = 1.38;
  }

  let targetUpper;
  let targetLower;
  const shoulderY = r.body.position.y + r.torsoH * 0.66;
  if (joint.startsWith('elbow')) {
    const x = Math.max(0.05, motion.contactX);
    const y = motion.contactY - shoulderY;
    targetUpper = Math.atan2(y, x) + Math.PI / 2;
    targetLower = 1.18;
  } else {
    solvePlanarAngles(
      IK_RESULT,
      r.upperArm,
      r.foreArm,
      Math.max(0.05, motion.contactX - r.h * 0.025),
      motion.contactY - shoulderY,
      motion.bend,
    );
    targetUpper = IK_RESULT.upper;
    targetLower = IK_RESULT.lower;
  }

  const preparedUpper = lerp(baseUpper, windUpper, wind);
  const preparedLower = lerp(baseLower, windLower, wind);
  arm.shoulder.rotation.z = lerp(preparedUpper, targetUpper, contact);
  arm.elbow.rotation.z = lerp(preparedLower, targetLower, contact);
  arm.shoulder.rotation.x = lerp(
    arm.shoulder.rotation.x,
    sideSign * motion.shoulderDepth,
    Math.max(contact, wind * 0.5),
  );
}

function poseStrikingLeg(r, leg, joint, motion, wind, contact) {
  const baseUpper = leg.hip.rotation.z;
  const baseLower = leg.knee.rotation.z;
  let windUpper = motion.arc === 'stomp' ? 0.72 : -motion.windup * 0.72;
  let windLower = motion.arc === 'stomp' ? 1.18 : 0.86 + motion.windup * 0.28;
  if (motion.arc === 'round-kick') {
    windUpper = -0.52;
    windLower = 1.02;
  } else if (motion.arc === 'low-kick') {
    windUpper = -0.28;
    windLower = 0.72;
  } else if (motion.arc === 'knee') {
    windUpper = -0.34;
    windLower = 1.38;
  }

  let targetUpper;
  let targetLower;
  const hipY = r.body.position.y;
  if (joint.startsWith('knee')) {
    const x = Math.max(0.05, motion.contactX);
    const y = motion.contactY - hipY;
    targetUpper = Math.atan2(y, x) + Math.PI / 2;
    targetLower = 1.34;
  } else {
    solvePlanarAngles(
      IK_RESULT,
      r.upperLeg,
      r.lowerLeg,
      Math.max(0.05, motion.contactX - r.h * 0.09),
      motion.contactY - hipY,
      motion.bend,
    );
    targetUpper = IK_RESULT.upper;
    targetLower = IK_RESULT.lower;
  }

  leg.hip.rotation.z = lerp(lerp(baseUpper, windUpper, wind), targetUpper, contact);
  leg.knee.rotation.z = lerp(lerp(baseLower, windLower, wind), targetLower, contact);
  leg.foot.rotation.z = -(r.body.rotation.z + leg.hip.rotation.z + leg.knee.rotation.z);
}

function plantFoot(r, leg) {
  leg.foot.rotation.z = -(r.body.rotation.z + leg.hip.rotation.z + leg.knee.rotation.z);
}

function solveLegTarget(r, leg, targetX, targetY, amount, bend = 1) {
  const blend = clamp(amount, 0, 1);
  const baseUpper = leg.hip.rotation.z;
  const baseLower = leg.knee.rotation.z;
  solvePlanarAngles(
    IK_RESULT,
    r.upperLeg,
    r.lowerLeg,
    targetX,
    targetY - r.body.position.y,
    bend,
  );
  // The body is the parent of the hip, so remove its lean from the solved world-plane angle.
  leg.hip.rotation.z = lerp(baseUpper, IK_RESULT.upper - r.body.rotation.z, blend);
  leg.knee.rotation.z = lerp(baseLower, IK_RESULT.lower, blend);
  plantFoot(r, leg);
}

function ankleHeight(r, leg) {
  const upperAngle = r.body.rotation.z + leg.hip.rotation.z;
  const lowerAngle = upperAngle + leg.knee.rotation.z;
  return r.body.position.y
    - Math.cos(upperAngle) * r.upperLeg
    - Math.cos(lowerAngle) * r.lowerLeg;
}

function groundBodyToLeg(r, leg, amount = 1) {
  const correction = r.restAnkleY - ankleHeight(r, leg);
  r.body.position.y += correction * clamp(amount, 0, 1);
  plantFoot(r, leg);
}

function groundBodyToFeet(r, amount = 1) {
  const average = (ankleHeight(r, r.legs.left) + ankleHeight(r, r.legs.right)) * 0.5;
  r.body.position.y += (r.restAnkleY - average) * clamp(amount, 0, 1);
  plantFoot(r, r.legs.left);
  plantFoot(r, r.legs.right);
}

function applyStanceTransition(r, contact, wind) {
  const amount = Math.max(contact, wind);
  r.body.position.y -= r.h * 0.045 * amount;
  r.body.rotation.z += 0.08 * amount;
  r.body.rotation.y += (r.isKestrel ? -0.28 : 0.16) * amount;
  r.legs.left.hip.rotation.z -= 0.22 * amount;
  r.legs.right.hip.rotation.z += 0.18 * amount;
  r.legs.left.knee.rotation.z += 0.38 * amount;
  r.legs.right.knee.rotation.z += 0.3 * amount;
  r.arms.left.shoulder.rotation.z = lerp(r.arms.left.shoulder.rotation.z, 1.08, amount);
  r.arms.right.shoulder.rotation.z = lerp(r.arms.right.shoulder.rotation.z, 0.72, amount);
  setHandCurl(r.arms.left.hand, 0.36, 0.1);
  setHandCurl(r.arms.right.hand, 0.36, -0.1);
  plantFoot(r, r.legs.left);
  plantFoot(r, r.legs.right);
}

function applyParryPose(r, contact, wind) {
  const amount = Math.max(contact, wind);
  r.body.rotation.z -= 0.07 * amount;
  r.body.rotation.y += 0.24 * amount;
  r.arms.left.shoulder.rotation.z = lerp(r.arms.left.shoulder.rotation.z, 1.42, amount);
  r.arms.left.elbow.rotation.z = lerp(r.arms.left.elbow.rotation.z, 0.34, amount);
  r.arms.right.shoulder.rotation.z = lerp(r.arms.right.shoulder.rotation.z, 0.64, amount);
  r.arms.right.elbow.rotation.z = lerp(r.arms.right.elbow.rotation.z, 1.08, amount);
  setHandCurl(r.arms.left.hand, 0.12, 0.12);
  setHandCurl(r.arms.right.hand, 0.22, -0.08);
  plantFoot(r, r.legs.left);
  plantFoot(r, r.legs.right);
}

function applyThrowGrip(r, f, opponent) {
  const gap = Math.abs(opponent.x - f.x);
  const reach = Math.min(r.upperArm + r.foreArm - 0.018, Math.max(0.38, gap * 0.7));
  r.body.rotation.z = 0.13;
  r.arms.left.shoulder.rotation.x = 0.52;
  r.arms.right.shoulder.rotation.x = -0.52;
  solvePlanarArm(r.arms.left, r.upperArm, r.foreArm, reach, -0.04, 1);
  solvePlanarArm(r.arms.right, r.upperArm, r.foreArm, reach * 0.94, -0.22, -1);
  setHandCurl(r.arms.left.hand, 0.38, 0.16);
  setHandCurl(r.arms.right.hand, 0.38, -0.16);
}

function throwPhase(out, attacker, profile) {
  const executing = attacker.state === 'throw-execute';
  out.catchAmount = executing ? 1 : smooth(attacker.throwTick / Math.max(1, profile.catchTicks));
  out.progress = executing
    ? clamp(attacker.throwTick / Math.max(1, profile.executeTicks), 0, 1)
    : 0;
  const liftProgress = clamp(out.progress / Math.max(0.01, profile.impactAt), 0, 1);
  out.lift = Math.sin(liftProgress * Math.PI);
  out.turn = smooth(liftProgress);
  out.impact = smooth(
    (out.progress - profile.impactAt) / Math.max(0.01, 1 - profile.impactAt),
  );
  return out;
}

function sampleThrowVictimPose(out, victim, profile, phase) {
  const height = victim.stats.height;
  const pelvisY = height * 0.49;
  const restRoll = victim.stats.id === 'kestrel' ? 0.055 : 0.025;
  out.compression = profile.victimCompression * phase.catchAmount;
  out.x = profile.victimShiftX * phase.catchAmount;
  out.y = pelvisY * (1 - out.compression * 0.34)
    + profile.victimApexY * phase.lift
    - phase.impact * height * 0.12;
  out.roll = lerp(
    restRoll + profile.victimHoldLean * phase.catchAmount,
    profile.victimRoll,
    phase.turn,
  );
  out.twist = lerp(
    profile.victimHoldTwist * phase.catchAmount,
    profile.victimTwist,
    phase.turn,
  );
  out.yaw = -profile.attackerTwist * phase.turn * 0.38;
  return out;
}

/**
 * Samples a grip point attached to the victim's rotating torso. Both rigs use this exact
 * transform, so hands continue to follow the caught waist/collar through lift and slam arcs.
 */
function sampleThrowGripTarget(out, attacker, victim, profile, phase, heightOffset = 0) {
  const pose = sampleThrowVictimPose(THROW_VICTIM_POSE, victim, profile, phase);
  const victimHeight = victim.stats.height;
  const localY = (profile.gripHeight - 0.49) * victimHeight + heightOffset;
  const gap = Math.abs(victim.x - attacker.x);
  out.x = gap - pose.x + Math.sin(pose.roll) * localY;
  out.y = pose.y + Math.cos(pose.roll) * localY;
  return out;
}

function applyPairedThrowAttacker(r, attacker, victim, profile) {
  const phase = throwPhase(THROW_PHASE, attacker, profile);
  const catchAmount = phase.catchAmount;
  const execution = phase.turn;
  const strain = clamp(catchAmount * 0.72 + phase.lift * 0.48, 0, 1);
  const brace = clamp(catchAmount * 0.55 + execution * 0.55, 0, 1);

  r.body.position.y -= r.h * profile.attackerCrouch * brace;
  r.body.position.x += profile.victimShiftX * execution * 0.28;
  r.body.rotation.z += profile.attackerLean * (catchAmount * 0.52 + execution * 0.62);
  r.body.rotation.y += profile.attackerTwist * execution;
  r.head.rotation.z -= profile.attackerLean * strain * 0.22;
  r.head.rotation.y -= profile.attackerTwist * execution * 0.28;
  r.chest.scale.y *= 1 + strain * 0.075;
  r.abdomen.scale.y *= 1 - strain * 0.035;

  r.legs.left.hip.rotation.z -= 0.2 * brace;
  r.legs.right.hip.rotation.z += 0.12 * brace;
  r.legs.left.knee.rotation.z += 0.52 * brace;
  r.legs.right.knee.rotation.z += 0.38 * brace;
  r.legs.left.hip.rotation.x -= profile.attackerTwist * execution * 0.12;
  r.legs.right.hip.rotation.x += profile.attackerTwist * execution * 0.1;
  groundBodyToFeet(r, brace);

  const victimHeight = victim.stats.height;
  const highOffset = profile.family === 'collar-drag' ? victimHeight * 0.12
    : profile.family === 'ankle-pick' ? victimHeight * 0.035
    : victimHeight * 0.055;
  const leftGrip = sampleThrowGripTarget(
    THROW_GRIP_TARGET,
    attacker,
    victim,
    profile,
    phase,
    highOffset,
  );
  solveBodySpaceArm(
    r,
    r.arms.left,
    leftGrip.x,
    leftGrip.y,
    1,
  );
  const rightGrip = sampleThrowGripTarget(
    THROW_GRIP_TARGET,
    attacker,
    victim,
    profile,
    phase,
    -highOffset,
  );
  solveBodySpaceArm(
    r,
    r.arms.right,
    rightGrip.x,
    rightGrip.y,
    -1,
  );
  r.arms.left.shoulder.rotation.x = profile.gripSpread * strain;
  r.arms.right.shoulder.rotation.x = -profile.gripSpread * strain;
  r.arms.left.hand.rotation.x = profile.attackerTwist * execution * 0.18;
  r.arms.right.hand.rotation.x = -profile.attackerTwist * execution * 0.16;
  setHandCurl(r.arms.left.hand, 0.5, 0.14);
  setHandCurl(r.arms.right.hand, 0.5, -0.14);
}

function applyPairedThrowVictim(r, victim, attacker, profile) {
  const phase = throwPhase(THROW_PHASE, attacker, profile);
  const catchAmount = phase.catchAmount;
  const execution = phase.turn;
  const airborne = phase.lift;
  const impact = phase.impact;
  const pose = sampleThrowVictimPose(THROW_VICTIM_POSE, victim, profile, phase);

  r.body.position.x = pose.x;
  r.body.position.y = pose.y;
  r.body.rotation.z = pose.roll;
  r.body.rotation.x = pose.twist;
  r.body.rotation.y = pose.yaw;
  r.head.rotation.z = -r.body.rotation.z * 0.2 - impact * 0.12;
  r.head.rotation.x = -r.body.rotation.x * 0.16;
  r.chest.scale.y *= 1 - pose.compression * 0.22;
  r.abdomen.scale.y *= 1 - pose.compression * 0.3;

  if (profile.family === 'ankle-pick') {
    poseAnklePickVictim(r, catchAmount, execution);
  } else if (profile.family === 'hold-iron') {
    poseBearhugVictim(r, catchAmount, execution);
  } else if (profile.family === 'hold-lift' || profile.family === 'hold-slam'
      || profile.family === 'pit-drop') {
    poseHoistedVictim(r, catchAmount, airborne, impact);
  } else {
    poseTurningThrowVictim(r, catchAmount, execution, airborne);
  }
  setHandCurl(r.arms.left.hand, lerp(0.72, 0.18, catchAmount), 0.16);
  setHandCurl(r.arms.right.hand, lerp(0.72, 0.18, catchAmount), -0.16);
}

function applyThrowReleaseAttacker(r, attacker, profile) {
  const fade = smooth(1 - clamp(attacker.stateTick / 60, 0, 1));
  const push = smooth(clamp(attacker.stateTick / 9, 0, 1)) * fade;
  const brace = clamp(fade * 0.82 + push * 0.3, 0, 1);
  r.body.position.y -= r.h * profile.attackerCrouch * fade * 0.42;
  r.body.rotation.z += profile.attackerLean * fade * 0.48 - 0.09 * push;
  r.body.rotation.y += profile.attackerTwist * fade * 0.55;
  r.head.rotation.z -= r.body.rotation.z * 0.18;
  r.chest.scale.y *= 1 + fade * 0.035;
  r.abdomen.scale.y *= 1 - fade * 0.018;
  r.legs.left.hip.rotation.z -= 0.16 * brace;
  r.legs.right.hip.rotation.z += 0.1 * brace;
  r.legs.left.knee.rotation.z += 0.38 * brace;
  r.legs.right.knee.rotation.z += 0.28 * brace;
  groundBodyToFeet(r, brace);

  const reach = r.upperArm + r.foreArm - 0.012;
  solvePlanarArm(r.arms.left, r.upperArm, r.foreArm, reach * 0.88, -0.1, 1);
  solvePlanarArm(r.arms.right, r.upperArm, r.foreArm, reach * 0.82, -0.22, -1);
  r.arms.left.shoulder.rotation.x = profile.gripSpread * fade * 0.38;
  r.arms.right.shoulder.rotation.x = -profile.gripSpread * fade * 0.38;
  r.arms.left.hand.rotation.z = -0.18 * push;
  r.arms.right.hand.rotation.z = 0.18 * push;
  setHandCurl(r.arms.left.hand, lerp(0.72, 0.08, fade), 0.24);
  setHandCurl(r.arms.right.hand, lerp(0.72, 0.08, fade), -0.24);
}

function poseTurningThrowVictim(r, caught, turn, lift) {
  r.arms.left.shoulder.rotation.z = lerp(0.82, -0.48, caught);
  r.arms.right.shoulder.rotation.z = lerp(0.62, 1.62, caught);
  r.arms.left.elbow.rotation.z = lerp(0.62, 0.24, caught);
  r.arms.right.elbow.rotation.z = lerp(0.74, 0.32, caught);
  r.arms.left.shoulder.rotation.x += 0.42 * caught;
  r.arms.right.shoulder.rotation.x -= 0.38 * caught;
  r.legs.left.hip.rotation.z = lerp(0.05, -0.52, turn);
  r.legs.right.hip.rotation.z = lerp(-0.02, 0.62, turn);
  r.legs.left.knee.rotation.z = lerp(0.06, 0.78 + lift * 0.3, turn);
  r.legs.right.knee.rotation.z = lerp(0.11, 0.42 + lift * 0.36, turn);
}

function poseAnklePickVictim(r, caught, turn) {
  r.arms.left.shoulder.rotation.z = lerp(0.82, -0.22, caught);
  r.arms.right.shoulder.rotation.z = lerp(0.62, 1.8, caught);
  r.arms.left.elbow.rotation.z = 0.3;
  r.arms.right.elbow.rotation.z = 0.24;
  r.legs.left.hip.rotation.z = lerp(0.05, -0.82, turn);
  r.legs.right.hip.rotation.z = lerp(-0.02, 0.92, turn);
  r.legs.left.knee.rotation.z = lerp(0.06, 1.18, caught);
  r.legs.right.knee.rotation.z = lerp(0.11, 0.34, caught);
}

function poseBearhugVictim(r, caught, turn) {
  r.arms.left.shoulder.rotation.z = lerp(0.82, 1.72, caught);
  r.arms.right.shoulder.rotation.z = lerp(0.62, 1.54, caught);
  r.arms.left.elbow.rotation.z = lerp(0.62, 1.18, caught);
  r.arms.right.elbow.rotation.z = lerp(0.74, 1.24, caught);
  r.arms.left.shoulder.rotation.x = 0.18 * caught;
  r.arms.right.shoulder.rotation.x = -0.18 * caught;
  r.legs.left.hip.rotation.z -= 0.22 * turn;
  r.legs.right.hip.rotation.z += 0.18 * turn;
  r.legs.left.knee.rotation.z += 0.38 * caught;
  r.legs.right.knee.rotation.z += 0.34 * caught;
}

function poseHoistedVictim(r, caught, lift, impact) {
  r.arms.left.shoulder.rotation.z = lerp(0.82, -0.64, caught);
  r.arms.right.shoulder.rotation.z = lerp(0.62, 1.84, caught);
  r.arms.left.shoulder.rotation.x = 0.52 * caught;
  r.arms.right.shoulder.rotation.x = -0.48 * caught;
  r.arms.left.elbow.rotation.z = 0.26;
  r.arms.right.elbow.rotation.z = 0.32;
  r.legs.left.hip.rotation.z = lerp(0.05, -0.72, caught);
  r.legs.right.hip.rotation.z = lerp(-0.02, 0.58, caught);
  r.legs.left.knee.rotation.z = lerp(0.06, 1.16 - impact * 0.32, caught);
  r.legs.right.knee.rotation.z = lerp(0.11, 0.76 + lift * 0.28, caught);
}

/**
 * Deterministic two-bone correction in the combat plane. It only changes the visual chain; the
 * authored throw volume remains authoritative. Angles are measured from the rig's hanging -Y
 * rest axis, hence the +PI/2 conversion from the conventional +X IK solution.
 */
function solvePlanarArm(arm, upper, lower, targetX, targetY, bend) {
  solvePlanarAngles(IK_RESULT, upper, lower, targetX, targetY, bend);
  arm.shoulder.rotation.z = IK_RESULT.upper;
  arm.elbow.rotation.z = IK_RESULT.lower;
}

function solveBodySpaceArm(r, arm, targetX, targetY, bend) {
  const dx = targetX - r.body.position.x;
  const dy = targetY - (r.body.position.y + r.torsoH * 0.66);
  const cosine = Math.cos(r.body.rotation.z);
  const sine = Math.sin(r.body.rotation.z);
  solvePlanarArm(
    arm,
    r.upperArm,
    r.foreArm,
    cosine * dx + sine * dy,
    -sine * dx + cosine * dy,
    bend,
  );
}

function solvePlanarAngles(out, upper, lower, targetX, targetY, bend) {
  const minReach = Math.abs(upper - lower) + 0.002;
  const maxReach = upper + lower - 0.002;
  const rawDistance = Math.sqrt(targetX * targetX + targetY * targetY);
  const distance = clamp(rawDistance, minReach, maxReach);
  const scale = rawDistance > 0.0001 ? distance / rawDistance : 1;
  const x = targetX * scale;
  const y = targetY * scale;
  const cosElbow = clamp(
    (distance * distance - upper * upper - lower * lower) / (2 * upper * lower),
    -1,
    1,
  );
  const elbow = Math.acos(cosElbow) * bend;
  const shoulder = Math.atan2(y, x)
    - Math.atan2(lower * Math.sin(elbow), upper + lower * Math.cos(elbow));
  out.upper = shoulder + Math.PI / 2;
  out.lower = elbow;
  return out;
}

function setHandCurl(hand, curl, spread) {
  const fingers = hand.userData.fingers;
  if (!fingers) return;
  for (let i = 0; i < fingers.length; i++) {
    const finger = fingers[i];
    const offset = (i - 1.5) * spread;
    finger.proximal.rotation.set(offset, 0, 0.16 + curl * 1.2);
    finger.distal.rotation.set(0, 0, curl * 1.34);
  }
  if (hand.userData.thumb) {
    hand.userData.thumb.rotation.set(spread * 1.8, 0, 0.42 + curl * 0.68);
  }
}

function kestrelIntro(r, t) {
  r.body.rotation.z = 0.12;
  r.arms.left.shoulder.rotation.z = 0.38;
  r.arms.right.shoulder.rotation.z = 1.54;
  r.arms.right.elbow.rotation.z = 0.5;
  r.head.rotation.z = -0.14 + Math.sin(t * 1.8) * 0.03;
}

function bulwarkIntro(r, t) {
  r.body.rotation.z = -0.11;
  r.arms.left.shoulder.rotation.z = 0.72;
  r.arms.right.shoulder.rotation.z = 0.72;
  r.arms.left.elbow.rotation.z = 1.15;
  r.arms.right.elbow.rotation.z = 1.15;
  r.head.rotation.z = 0.08 + Math.sin(t * 1.2) * 0.02;
}

function smooth(t) {
  t = clamp(t, 0, 1);
  return t * t * (3 - 2 * t);
}

function mesh(parent, geometry, material, x = 0, y = 0, z = 0, shadows = false) {
  const out = new THREE.Mesh(geometry, material);
  out.position.set(x, y, z);
  out.castShadow = shadows;
  out.receiveShadow = shadows;
  parent.add(out);
  return out;
}

function boxGeo(x, y, z, bag) {
  const g = new THREE.BoxGeometry(x, y, z, 1, 1, 1);
  bag.geometry(g);
  return g;
}

function layeredPanelGeo(thickness, height, width, endScale, bow, bag) {
  const g = new THREE.BoxGeometry(thickness, height, width, 1, 3, 2);
  const positions = g.getAttribute('position');
  const halfHeight = height * 0.5;
  for (let i = 0; i < positions.count; i++) {
    const normalizedY = clamp(positions.getY(i) / halfHeight, -1, 1);
    const end = smooth(clamp((Math.abs(normalizedY) - 0.34) / 0.66, 0, 1));
    const center = 1 - normalizedY * normalizedY;
    positions.setX(i, positions.getX(i) + thickness * bow * center);
    positions.setZ(i, positions.getZ(i) * lerp(1, endScale, end));
  }
  positions.needsUpdate = true;
  g.computeVertexNormals();
  bag.geometry(g);
  return g;
}

function armorPlateGeo(length, height, depth, bag) {
  const g = new THREE.BoxGeometry(length, height, depth, 3, 2, 2);
  const positions = g.getAttribute('position');
  const halfLength = length * 0.5;
  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const normalizedX = clamp(x / halfLength, -1, 1);
    const center = smooth(1 - Math.abs(normalizedX));
    positions.setY(i, y + height * center * (y > 0 ? 0.16 : 0.035));
    positions.setZ(i, positions.getZ(i) * lerp(0.72, 1, center));
  }
  positions.needsUpdate = true;
  g.computeVertexNormals();
  bag.geometry(g);
  return g;
}

function shapedHeadGeo(radius, isKestrel, widthSegments, heightSegments, bag) {
  const g = new THREE.SphereGeometry(radius, widthSegments, heightSegments);
  const positions = g.getAttribute('position');
  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);
    const normalizedY = clamp(y / radius, -1, 1);
    const jaw = smooth(clamp((-normalizedY - 0.04) / 0.96, 0, 1));
    const cheek = 1 - Math.min(1, Math.abs(normalizedY + 0.08) * 2.2);
    const jawWidth = isKestrel
      ? lerp(1, 0.7, jaw)
      : lerp(1.04 + cheek * 0.035, 0.84, jaw);
    const crown = clamp((normalizedY - 0.66) / 0.34, 0, 1);
    positions.setX(i, x + radius * jaw * (isKestrel ? 0.045 : 0.11));
    positions.setY(i, y - radius * crown * (isKestrel ? 0.035 : 0.07));
    positions.setZ(i, z * jawWidth);
  }
  positions.needsUpdate = true;
  g.computeVertexNormals();
  bag.geometry(g);
  return g;
}

function kestrelHoodGeo(radius, widthSegments, heightSegments, bag) {
  const g = new THREE.SphereGeometry(radius, widthSegments, heightSegments);
  const positions = g.getAttribute('position');
  for (let i = 0; i < positions.count; i++) {
    const x = positions.getX(i);
    const y = positions.getY(i);
    const z = positions.getZ(i);
    const rear = clamp(-x / radius, 0, 1);
    const crown = clamp(y / radius, 0, 1);
    positions.setX(i, x * (1 + rear * 0.2) - radius * crown * 0.035);
    positions.setZ(i, z * (1 - crown * 0.1 + rear * 0.045));
  }
  positions.needsUpdate = true;
  g.computeVertexNormals();
  bag.geometry(g);
  return g;
}

function facePlateGeo(width, height, depth, bottomScale, bag) {
  const g = new THREE.BoxGeometry(width, height, depth, 1, 2, 1);
  const positions = g.getAttribute('position');
  const halfHeight = height * 0.5;
  for (let i = 0; i < positions.count; i++) {
    const normalizedY = clamp((positions.getY(i) + halfHeight) / height, 0, 1);
    const lower = 1 - normalizedY;
    positions.setX(i, positions.getX(i) + width * lower * 0.1);
    positions.setZ(i, positions.getZ(i) * lerp(bottomScale, 1, normalizedY));
  }
  positions.needsUpdate = true;
  g.computeVertexNormals();
  bag.geometry(g);
  return g;
}

function jawBlockGeo(width, height, depth, bottomScale, bag) {
  const g = new THREE.BoxGeometry(width, height, depth, 1, 2, 1);
  const positions = g.getAttribute('position');
  const halfHeight = height * 0.5;
  for (let i = 0; i < positions.count; i++) {
    const normalizedY = clamp((positions.getY(i) + halfHeight) / height, 0, 1);
    const lower = 1 - normalizedY;
    positions.setX(i, positions.getX(i) + width * lower * 0.18);
    positions.setZ(i, positions.getZ(i) * lerp(bottomScale, 1, normalizedY));
  }
  positions.needsUpdate = true;
  g.computeVertexNormals();
  bag.geometry(g);
  return g;
}

function wedgeGeo(x, y, z, bag) {
  const g = new THREE.BoxGeometry(x, y, z, 1, 1, 1);
  const positions = g.getAttribute('position');
  for (let i = 0; i < positions.count; i++) {
    if (positions.getX(i) > 0 && positions.getY(i) > 0) {
      positions.setY(i, positions.getY(i) * 0.28);
    }
  }
  positions.needsUpdate = true;
  g.computeVertexNormals();
  bag.geometry(g);
  return g;
}

/**
 * Closed radial volume with an authored silhouette profile.
 *
 * Constant-radius capsules made every arm, leg and torso read as stacked tubing. These profiles
 * retain the exact joint endpoints while introducing ribcage taper, biceps/forearm mass, thigh
 * fullness and a calf-to-ankle break. `centered` is used by torso pieces; limbs begin at their
 * parent joint and extend down to the child joint.
 */
function profiledRadialGeo(length, radius, profile, radial, centered, bag) {
  const columns = radial + 1;
  const positions = [];
  const uvs = [];
  const indices = [];
  for (let row = 0; row < profile.length; row++) {
    const [t, scale] = profile[row];
    const y = centered ? (0.5 - t) * length : -t * length;
    const ringRadius = radius * scale;
    for (let segment = 0; segment <= radial; segment++) {
      const u = segment / radial;
      const angle = u * Math.PI * 2;
      positions.push(Math.cos(angle) * ringRadius, y, Math.sin(angle) * ringRadius);
      uvs.push(u, t);
    }
  }
  for (let row = 0; row < profile.length - 1; row++) {
    const next = row + 1;
    for (let segment = 0; segment < radial; segment++) {
      const a = row * columns + segment;
      const b = next * columns + segment;
      indices.push(a, a + 1, b, a + 1, b + 1, b);
    }
  }

  const topY = centered ? length * 0.5 : 0;
  const bottomY = centered ? -length * 0.5 : -length;
  const topCenter = positions.length / 3;
  positions.push(0, topY, 0);
  uvs.push(0.5, 0.5);
  const bottomCenter = topCenter + 1;
  positions.push(0, bottomY, 0);
  uvs.push(0.5, 0.5);
  const topCapStart = positions.length / 3;
  const topRadius = radius * profile[0][1];
  for (let segment = 0; segment <= radial; segment++) {
    const angle = segment / radial * Math.PI * 2;
    const x = Math.cos(angle) * topRadius;
    const z = Math.sin(angle) * topRadius;
    positions.push(x, topY, z);
    uvs.push(0.5 + x / (topRadius * 2), 0.5 + z / (topRadius * 2));
  }
  const bottomCapStart = positions.length / 3;
  const bottomRadius = radius * profile[profile.length - 1][1];
  for (let segment = 0; segment <= radial; segment++) {
    const angle = segment / radial * Math.PI * 2;
    const x = Math.cos(angle) * bottomRadius;
    const z = Math.sin(angle) * bottomRadius;
    positions.push(x, bottomY, z);
    uvs.push(0.5 + x / (bottomRadius * 2), 0.5 + z / (bottomRadius * 2));
  }
  for (let segment = 0; segment < radial; segment++) {
    indices.push(topCenter, topCapStart + segment + 1, topCapStart + segment);
    indices.push(bottomCenter, bottomCapStart + segment, bottomCapStart + segment + 1);
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex(indices);
  g.computeVertexNormals();
  bag.geometry(g);
  return g;
}

function footSoleGeo(length, height, width, bag) {
  const g = new THREE.BoxGeometry(length, height, width, 3, 1, 1);
  const positions = g.getAttribute('position');
  const half = length * 0.5;
  for (let i = 0; i < positions.count; i++) {
    const normalizedX = positions.getX(i) / half;
    // Narrow the heel and toe while retaining the full-width planted midfoot. This keeps the sole
    // readable at combat distance without the old flat rectangular-board silhouette.
    const widthScale = normalizedX < -0.35
      ? lerp(0.7, 0.92, (normalizedX + 1) / 0.65)
      : normalizedX > 0.3
        ? lerp(1, 0.78, (normalizedX - 0.3) / 0.7)
        : 1;
    positions.setZ(i, positions.getZ(i) * widthScale);
  }
  positions.needsUpdate = true;
  g.computeVertexNormals();
  bag.geometry(g);
  return g;
}

function shapedPalmGeo(radius, side, widthSegments, heightSegments, bag) {
  const g = new THREE.SphereGeometry(radius, widthSegments, heightSegments);
  const positions = g.getAttribute('position');
  for (let i = 0; i < positions.count; i++) {
    const y = clamp(positions.getY(i) / radius, -1, 1);
    const z = positions.getZ(i);
    // Hands are broadest across the knuckles and narrower at the wrist. A restrained asymmetric
    // pad on the thumb side keeps the two mirrored hands anatomical without adding another mesh.
    const knuckleWidth = lerp(1.05, 0.9, (y + 1) * 0.5);
    const thumbSide = clamp((z * side) / radius, 0, 1);
    positions.setZ(i, z * knuckleWidth * (1 + thumbSide * 0.06));
  }
  positions.needsUpdate = true;
  g.computeVertexNormals();
  bag.geometry(g);
  return g;
}

function sphereGeo(r, w, h, bag) {
  const g = new THREE.SphereGeometry(r, w, h);
  bag.geometry(g);
  return g;
}

function capsuleGeo(r, total, radial, bag) {
  const g = new THREE.CapsuleGeometry(r, Math.max(0.015, total - r * 2), 4, radial);
  bag.geometry(g);
  return g;
}

function cylinderGeo(rt, rb, h, radial, bag) {
  const g = new THREE.CylinderGeometry(rt, rb, h, radial, 1);
  bag.geometry(g);
  return g;
}

function coneGeo(r, h, radial, bag) {
  const g = new THREE.ConeGeometry(r, h, radial, 1);
  bag.geometry(g);
  return g;
}

function torusGeo(r, tube, radial, tubular, bag) {
  const g = new THREE.TorusGeometry(r, tube, radial, tubular);
  bag.geometry(g);
  return g;
}
